import React from "react";
import ComparisonPageTemplate from "../../components/ComparisonPageTemplate/ComparisonPageTemplate";

const CANONICAL_URL = "https://teamgrid.ai/compare/teamgrid-ai-vs-insightful";

const FAQ_ITEMS = [
  {
    question: "How is TeamGrid different from Insightful?",
    answer:
      "TeamGrid focuses on helping leaders understand why work is progressing the way it is using AI to surface bottlenecks, trends, and risks. Insightful tracks time, activity, and screen usage. TeamGrid goes beyond data to deliver intelligence and action, making it the Best Insightful Alternative for insight-led teams.",
  },
  {
    question: "What is Ask TeamGrid?",
    answer:
      "Ask TeamGrid is an AI chat feature that gives instant clarity into questions like: What's slowing down the team? Who's overloaded right now? Where can we improve collaboration? Instead of analyzing dashboards, leaders get clear, downloadable reports in seconds, a major reason why many consider it one of the most useful Insightful alternatives for businesses.",
  },
  {
    question: "Is TeamGrid suitable for enterprise teams?",
    answer:
      "Yes. TeamGrid is built for scalability across departments and regions making it a reliable Insightful alternative for growing teams, remote organizations, and multi-team structures.",
  },
  {
    question: "Does TeamGrid work for remote and hybrid teams?",
    answer:
      "Absolutely. TeamGrid provides consistent visibility across remote, hybrid, and in-office teams with no reliance on surveillance or invasive tracking.",
  },
  {
    question: "Is there a Free version or trial of TeamGrid?",
    answer:
      "Yes! TeamGrid offers a full-featured Free Trial making it the Best Free Insightful Alternative for teams who want to try insight-led intelligence without upfront commitment.",
  },
];

const JSON_LD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL_URL}#faq`,
  mainEntity: [
    {
      "@type": "Question",
      name: "How is TeamGrid different from Insightful?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid focuses on helping leaders understand why work is progressing the way it is using AI to surface bottlenecks, trends, and risks. Insightful tracks time, activity, and screen usage. TeamGrid goes beyond data to deliver intelligence and action for insight-led teams.",
      },
    },
    {
      "@type": "Question",
      name: "What is Ask TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Ask TeamGrid is an AI chat feature that provides instant clarity into questions like what's slowing down the team, who's overloaded, and where collaboration can improve. Leaders get clear, downloadable reports in seconds without manual dashboard analysis.",
      },
    },
    {
      "@type": "Question",
      name: "Is TeamGrid suitable for enterprise teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid is built to scale across departments and regions, making it suitable for growing organizations and multi-team structures that want privacy-first, insight-led visibility.",
      },
    },
    {
      "@type": "Question",
      name: "Does TeamGrid work for remote and hybrid teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. TeamGrid provides consistent visibility across remote, hybrid, and in-office teams without relying on surveillance or invasive tracking.",
      },
    },
    {
      "@type": "Question",
      name: "Is there a free version or trial of TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid offers a full-featured free trial so teams can experience insight-led intelligence without upfront commitment.",
      },
    },
  ],
};

const JSON_LD_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://teamgrid.ai/#website",
      url: "https://teamgrid.ai/",
      name: "TeamGrid",
    },
    {
      "@type": "Organization",
      "@id": "https://teamgrid.ai/#organization",
      name: "TeamGrid",
      url: "https://teamgrid.ai/",
    },
    {
      "@type": "WebPage",
      "@id": `${CANONICAL_URL}#webpage`,
      url: CANONICAL_URL,
      name: "Looking for an Insightful Alternative That Goes Beyond Tracking? Meet TeamGrid",
      description: "TeamGrid doesn't just record data, it reveals what matters. A privacy-first Insightful alternative that turns workforce visibility into intelligent action with AI-powered insights.",
      isPartOf: { "@id": "https://teamgrid.ai/#website" },
      about: [
        { "@id": "https://teamgrid.ai/#teamgrid-app" },
        { "@id": "https://insightful.io/#insightful-app" },
      ],
      breadcrumb: { "@id": `${CANONICAL_URL}#breadcrumbs` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL_URL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://teamgrid.ai/" },
        { "@type": "ListItem", position: 2, name: "Compare", item: "https://teamgrid.ai/compare/" },
        { "@type": "ListItem", position: 3, name: "TeamGrid vs Insightful", item: CANONICAL_URL },
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://teamgrid.ai/#teamgrid-app",
      name: "TeamGrid",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS",
      description: "TeamGrid is an AI-powered workforce intelligence platform that helps leaders understand how and why work happens using privacy-first insights, summaries, and organizational intelligence—without employee surveillance.",
      url: "https://teamgrid.ai/",
      publisher: { "@id": "https://teamgrid.ai/#organization" },
      featureList: [
        "Understand how and why work happens (insight-led visibility)",
        "AI-powered insights to identify patterns, risks, and opportunities",
        "Ask TeamGrid (conversational AI) for instant answers",
        "Privacy-first design (no hidden tracking or surveillance)",
        "Organizational Intelligence Engine surfaces bottlenecks and burnout risk",
        "Daily, hourly, and weekly work summaries",
        "One-click, presentation-ready report downloads",
        "Works across team types with easy onboarding",
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://insightful.io/#insightful-app",
      name: "Insightful",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS, Web",
      description: "Insightful is a workforce analytics and monitoring tool focused on time and activity visibility, including app tracking and monitoring options such as screenshots.",
      url: "https://insightful.io/",
      publisher: { "@type": "Organization", name: "Insightful", url: "https://insightful.io/" },
      featureList: [
        "Time and activity-based visibility",
        "Reporting-driven analytics",
        "Real-time and historical dashboards",
        "Exports including CSV and PDF",
        "Monitoring tools such as app tracking and screenshots (optional)",
      ],
    },
    {
      "@type": "ItemList",
      "@id": `${CANONICAL_URL}#comparison`,
      name: "TeamGrid vs Insightful - Feature Comparison",
      description: "Comparison of TeamGrid and Insightful across philosophy, AI insights, conversational AI, privacy approach, visibility model, dashboards, organizational intelligence, summaries, and reporting.",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: 2,
      itemListElement: [
        { "@type": "ListItem", position: 1, item: { "@id": "https://teamgrid.ai/#teamgrid-app" } },
        { "@type": "ListItem", position: 2, item: { "@id": "https://insightful.io/#insightful-app" } },
      ],
    },
  ],
};

const INSIGHTFUL_TABLE_ROWS = [
  { feature: "Core Philosophy", teamgrid: "Understand how and why work happens", competitor: "Monitor how time and activity are spent" },
  { feature: "AI-Powered Insights", teamgrid: "Identifies patterns, risks, and opportunities automatically", competitor: "Reporting-driven analytics, mostly manual" },
  { feature: "Ask TeamGrid (AI Chat)", teamgrid: "Ask TeamGrid for instant answers", competitor: "Not available" },
  { feature: "Privacy-Oriented Design", teamgrid: "Transparent, trust-driven, no hidden tracking", competitor: "Includes monitoring tools like app tracking & screenshots" },
  { feature: "Approach to Visibility", teamgrid: "Insight-led and outcome-focused", competitor: "Activity and time-based visibility" },
  { feature: "Dashboards", teamgrid: "Real-time clarity with summaries and patterns", competitor: "Real-time and historical dashboards" },
  { feature: "Organizational Insights", teamgrid: "Surfaces bottlenecks, burnout risk and inefficiencies", competitor: "Usage and productivity trends" },
  { feature: "Work Summaries", teamgrid: "Daily, hourly, and weekly summaries", competitor: "Time-use and behavior summaries" },
  { feature: "Export & Sharing", teamgrid: "One-click report downloads", competitor: "Multi-format exports including CSV and PDF" },
];

const DATA_DECISIONS_TEAMGRID_BULLETS = [
  "AI highlights slowdowns, blockers, and overcapacity",
  "Ask TeamGrid reveals exactly where attention is needed",
  "Leaders make better decisions faster with less effort",
];

const DATA_DECISIONS_INSIGHTFUL_BULLETS = [
  "Tracks productivity based on system usage and time spent",
  "Requires interpretation and manual follow-up",
  "Tools may give data but not direction",
];

const DATA_DECISIONS_2_TEAMGRID_BULLETS = [
  "Visibility comes from AI-analyzed patterns, not observation",
  "Helps managers see what's happening",
  "Drives high team adoption and transparency from day one",
  "This is why many companies now seek Insightful alternatives for businesses that focus on trust and improvement not control.",
];

const DATA_DECISIONS_2_INSIGHTFUL_BULLETS = [
  "Monitoring includes apps used, websites visited, and optional screenshots",
  "May feel invasive to employees, especially in hybrid teams",
  "Requires cultural adaptation and change management",
];

const INSIGHTFUL_WHAT_YOU_GET_ITEMS = [
  "Ask TeamGrid (AI chat for reports and insights)",
  "Organizational Intelligence Engine",
  "Real-time dashboards",
  "Daily, hourly, and weekly summaries",
  "One-click, presentation ready reports",
  "Works across all team types",
  "Easy onboarding",
  "Designed for privacy-first Insightful alternative experiences",
];

const KEY_FEATURE_CARDS = [
  {
    title: "Ask TeamGrid – Your AI Workforce Assistant",
    intro: "Leaders get instant clarity into what's slowing the team, who's overloaded, and where collaboration can improve—with clear, downloadable reports in seconds.",
    bullets: [],
    closing: "",
  },
  {
    title: "Organizational Intelligence, Not Just Monitoring",
    intro: "TeamGrid surfaces bottlenecks, burnout risk, and inefficiencies automatically—without surveillance or invasive tracking.",
    bullets: [],
    closing: "",
  },
];

const InsightfulComparison = () => (
  <ComparisonPageTemplate
    seoTitle="Insightful Alternative | AI-Powered Workforce Insights by TeamGrid"
    seoDescription="Looking for an Insightful alternative? TeamGrid delivers AI-driven workforce intelligence without surveillance built for trust, clarity, and action."
    seoCanonical={CANONICAL_URL}
    jsonLdGraph={JSON_LD_GRAPH}
    jsonLdFaq={JSON_LD_FAQ}
    heroBadge="Comparison Guide"
    heroTitleLine1="Looking for an Insightful Alternative That Goes Beyond Tracking?"
    heroTitleLine2="Meet TeamGrid"
    heroTitleLine2Teal
    heroTitleLine3={null}
    heroTitleLine4={null}
    heroDescription="TeamGrid doesn't just record data, it reveals what matters. The insightful alternative that turns workforce visibility into intelligent action."
    competitorName="Insightful"
    competitorCardLabel="Insightful"
    competitorLogoUrl="/logo_3rd_comp.webp"
    trustedBadges={[
      "Real workforce insights not just activity logs",
      "Privacy-first by design",
      "Ask TeamGrid: simple AI chat for instant team reports",
    ]}
    faqs={FAQ_ITEMS}
    faqTitle="Frequently Asked Questions"
    faqSubtitle="Common questions about TeamGrid vs. Insightful."
    comparisonTableRows={INSIGHTFUL_TABLE_ROWS}
    dataDecisionsHeading="Use Case 1: Getting Visibility Without Crossing Boundaries"
    dataDecisionsSubtitle="Why Organizations Choose the Insightful Alternative for Privacy-Respectful Insight"
    dataDecisionsTeamGridBullets={DATA_DECISIONS_TEAMGRID_BULLETS}
    dataDecisionsCompetitorBullets={DATA_DECISIONS_INSIGHTFUL_BULLETS}
    dataDecisionsCompetitorCardTitle="Insightful"
    dataDecisions2Heading="Use Case 2: From Monitoring to Meaningful Insight"
    dataDecisions2Subtitle="Why Teams Look for the Best Insightful Alternative to Make Better, Faster Decisions"
    dataDecisions2TeamGridBullets={DATA_DECISIONS_2_TEAMGRID_BULLETS}
    dataDecisions2CompetitorBullets={DATA_DECISIONS_2_INSIGHTFUL_BULLETS}
    dataDecisions2CompetitorCardTitle="Insightful"
    keyFeaturesTitle="Why Teams Choose TeamGrid Over Insightful"
    keyFeaturesSubtitle="Ask TeamGrid – Your AI Workforce Assistant"
    keyFeatureCards={KEY_FEATURE_CARDS}
    whatYouGetItems={INSIGHTFUL_WHAT_YOU_GET_ITEMS}
    outcomesTitle="Performance Outcomes Teams See"
    dataDecisionsSwapLayout={true}
    dataDecisionsSectionsReversed={true}
    dataDecisionsImageSrc="/left_border_1.webp"
    dataDecisions2ImageSrc="/Background+Border_bg_middle.webp"
    outcomesImageSrc="/Section_insightful_outcomes.webp"
  />
);

export default InsightfulComparison;
